/**
 * Which tax summary the page is showing, as it lives in the URL.
 *
 * IN THE URL, for the reason the statement's owner and month are: this is a document somebody
 * produces for a specific owner and year, sends to a colleague to check, and comes back to in
 * October when the owner's accountant finally opens it. Component state survives none of those.
 * It also makes the query key, so changing the year starts a fresh read rather than showing last
 * year's figures under this year's heading while the new one loads.
 *
 * ITS OWN MODULE, AND THE SPLIT IS A BUNDLE DECISION — the same one `statementSearch.ts` records.
 * `validateSearch` runs during ROUTING, so whatever this file imports lands in the eager bundle on
 * every page of the app. It therefore imports NOTHING, not even `taxYearFor` from core. Anything
 * richer belongs in `taxSummary.ts`, which is only reached from this report's own chunk.
 */

export interface TaxSummarySelection {
  /** The owner's ref sequence — `OW000003` is `3`. Absent until somebody picks one. */
  owner?: number;
  /**
   * The tax year, NAMED BY THE YEAR IT ENDS. 2026 is 1 April 2025 to 31 March 2026.
   *
   * Absent means "the one that has most recently finished", resolved by the screen — which needs
   * today's New Zealand date and is therefore not a decision this file can make.
   */
  year?: number;
}

/**
 * Read the selection out of the URL.
 *
 * SHAPE-CHECKED ONLY, like the statement's and the audit report's. A year this agency has no books
 * for is answered by an empty summary, which is a truthful document; the contract at the API
 * boundary — which a pasted URL crosses anyway — is what refuses a number that is not a year.
 *
 * The bounds here are the loosest thing that is still a four-digit year, deliberately: anything
 * tighter would be a second opinion about what the server already checks, and the two would drift.
 */
export function parseTaxSummarySearch(search: Record<string, unknown>): TaxSummarySelection {
  const owner = Number(search.owner);
  const year = Number(search.year);

  return {
    ...(Number.isSafeInteger(owner) && owner > 0 ? { owner } : {}),
    ...(Number.isSafeInteger(year) && year >= 1900 && year <= 2999 ? { year } : {}),
  };
}
